import React, { useState } from 'react';
import { DEFAULTS } from './constants';

// Grouped preset canvas sizes for common design formats.
// Dimensions are in pixels at screen/web resolution (72–96 DPI).
const PRESET_GROUPS = [
  {
    label: 'Social Media',
    presets: [
      { label: 'Instagram Post',       w: 1080, h: 1080 },
      { label: 'Instagram Story/Reel', w: 1080, h: 1920 },
      { label: 'Facebook Post',        w: 1200, h:  630 },
      { label: 'Facebook Cover',       w:  820, h:  312 },
      { label: 'YouTube Thumbnail',    w: 1280, h:  720 },
      { label: 'Twitter / X Post',     w: 1200, h:  675 },
      { label: 'LinkedIn Post',        w: 1200, h:  627 },
    ],
  },
  {
    label: 'Print',
    presets: [
      { label: 'Business Card',        w:  525, h:  300 },
      { label: 'Postcard (4 × 6 in)',  w:  576, h:  384 },
      { label: 'Postcard (5 × 7 in)',  w:  672, h:  480 },
      { label: 'Flyer / A4 Portrait',  w:  595, h:  842 },
      { label: 'Flyer / US Letter',    w:  612, h:  792 },
      { label: 'Poster / A3 Portrait', w:  842, h: 1191 },
    ],
  },
  {
    label: 'Presentation',
    presets: [
      { label: 'Widescreen 16:9',      w: 1280, h:  720 },
      { label: 'Standard 4:3',         w: 1024, h:  768 },
      { label: 'Square',               w:  800, h:  800 },
    ],
  },
];

// Modular popup for canvas size reset
export default function CanvasSizePopup({ aspectRatio, initialWidth, initialHeight, onApply, onClose }) {
  function parseAspectRatio(ratio) {
    if (!ratio) return 16 / 9;
    const [w, h] = ratio.split(':').map(Number);
    return w && h ? w / h : 16 / 9;
  }

  const [width, setWidth]   = useState(initialWidth  || DEFAULTS.CANVAS_MAX_W);
  const [height, setHeight] = useState(initialHeight || Math.round((initialWidth || DEFAULTS.CANVAS_MAX_W) / parseAspectRatio(aspectRatio)));
  const [preset, setPreset] = useState('');

  const derivedAspect = height > 0 ? (width / height).toFixed(2) : '—';

  const handlePresetChange = (e) => {
    const val = e.target.value;
    setPreset(val);
    if (!val) return;
    const [pw, ph] = val.split('x').map(Number);
    setWidth(pw);
    setHeight(ph);
  };

  const handleWidthChange = (e) => {
    setPreset('');
    setWidth(parseInt(e.target.value) || DEFAULTS.CANVAS_MAX_W);
  };

  const handleHeightChange = (e) => {
    setPreset('');
    setHeight(parseInt(e.target.value) || DEFAULTS.CANVAS_MAX_H);
  };

  const handleApply = () => {
    const ar = `${width}:${height}`;
    onApply(width, height, ar);
    onClose();
  };

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.2)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Canvas Size</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">

            {/* Preset picker */}
            <div className="mb-3">
              <label className="form-label fw-semibold">Preset Sizes</label>
              <select className="form-select" value={preset} onChange={handlePresetChange}>
                <option value="">— Custom —</option>
                {PRESET_GROUPS.map(group => (
                  <optgroup key={group.label} label={group.label}>
                    {group.presets.map(p => (
                      <option key={p.label} value={`${p.w}x${p.h}`}>
                        {p.label} ({p.w} × {p.h})
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <hr className="my-2" />

            {/* Manual width / height */}
            <div className="row g-2 mb-2">
              <div className="col">
                <label className="form-label">Width (px)</label>
                <input
                  type="number"
                  className="form-control"
                  min={DEFAULTS.CANVAS_MIN_W}
                  max={DEFAULTS.CANVAS_MAX_W}
                  value={width}
                  onChange={handleWidthChange}
                />
              </div>
              <div className="col">
                <label className="form-label">Height (px)</label>
                <input
                  type="number"
                  className="form-control"
                  min={DEFAULTS.CANVAS_MIN_H}
                  max={DEFAULTS.CANVAS_MAX_H}
                  value={height}
                  onChange={handleHeightChange}
                />
              </div>
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Aspect ratio: {derivedAspect} &nbsp;({width} × {height} px)
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleApply}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}

